import React from 'react';
import { Box, TableSortLabel } from '@mui/material';

import { TableProps } from './data-table.types';

import { Typography } from '../typography/typography.component';
import { Pagination } from '../../navigation/pagination/pagination.component';
import { EmptyDataTable } from '../empty-data-table/empty-data-table.component';
import { CircularProgress } from '../../feedback/circular-progress/circular-progress.component';
import {
  Table as MUITable,
  TableBody as MUITableBody,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  TableContainer as MUITableContainer,
} from '../table/table.component';
import { TableCell as MUITableCell } from '../table/table-cell.component';

export const DataTable = ({
  headers,
  loading,
  rows,
  error,
  paginationProps,
  sortingProps,
}: TableProps) => {
  if (error) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='something-went-wrong-msg'>
          Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!rows) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='no-data-msg'>
          No data. Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return <EmptyDataTable />;
  }

  const tableRows = rows.map((row, rowIndex) => {
    const onClick: (event: React.MouseEvent<HTMLTableRowElement>) => void = (event) => {
      event.preventDefault();
      if (row.onClick) {
        row.onClick();
      }
    };

    return (
      <MUITableRow
        key={row.key || rowIndex}
        onClick={onClick}
        sx={{ cursor: row.onClick ? 'pointer' : undefined }}
      >
        {row.fields.map(({ label, value, width, align, sx }, cellIndex) => (
          <MUITableCell
            key={label || cellIndex}
            width={width}
            align={align}
            sx={{
              overflowWrap: 'anywhere',
              ...sx,
            }}
          >
            {value}
          </MUITableCell>
        ))}
      </MUITableRow>
    );
  });

  const tableHeader = headers.map((header, index) => {
    const { label, align, sortingField } = header;
    if (sortingField) {
      return (
        <MUITableCell key={label || index} align={align} sx={{ whiteSpace: 'nowrap' }}>
          <TableSortLabel
            active={sortingField === sortingProps?.field}
            direction={sortingProps?.direction}
            onClick={
              sortingProps?.onClick ? () => sortingProps?.onClick?.(sortingField) : undefined
            }
          >
            {label}
          </TableSortLabel>
        </MUITableCell>
      );
    }
    return (
      <MUITableCell key={label || index} align={align} sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </MUITableCell>
    );
  });

  return (
    <MUITableContainer>
      <MUITable>
        <MUITableHead>
          <MUITableRow>{tableHeader}</MUITableRow>
        </MUITableHead>
        <MUITableBody>{tableRows}</MUITableBody>
      </MUITable>
      {paginationProps && <Pagination {...paginationProps} />}
    </MUITableContainer>
  );
};
